import React, { useState, useEffect, useRef, useCallback } from "react";
import { Download, Upload, ArrowLeft, Loader, ChevronLeft, ChevronRight } from "lucide-react";
import { postBackgroundRemove, postBackgroundSwap } from "../../../api/apiClient";
import { backgroundImages } from "../virtualAiIModel/VirtualAiModelData";
import { useAuth } from "../../../AuthProvider";

export const BackgroundRemoverResult = ({
  processedImage,
  originalImage,
  isLoading,
  onBackClick,
  onNewUpload,
}) => {
  const [displayImage, setDisplayImage] = useState(processedImage);
  const [loading, setLoading] = useState(isLoading);
  const [selectedBackground, setSelectedBackground] = useState(null);
  // const [backgroundPrompt, setBackgroundPrompt] = useState("");
  const [swapInProgress, setSwapInProgress] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [removedBgImage, setRemovedBgImage] = useState(processedImage);
  // eslint-disable-next-line no-unused-vars
  const [customBackground, setCustomBackground] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [imageLoaded, setImageLoaded] = useState(false);
  const fileInputRef = useRef(null);
  const newImageInputRef = useRef(null);

   const { currentUser } = useAuth();

   useEffect(() => {
    if (processedImage) {
      setRemovedBgImage(processedImage);
      setDisplayImage(processedImage);
      setLoading(false);
      
      // Added image loading detection
      const img = new Image();
      img.src = processedImage;
      img.onload = () => setImageLoaded(true);
    } else {
      setImageLoaded(false);
    }
  }, [processedImage]);

  const handleBackgroundSelect = async (backgroundImageUrl) => {
    try {
      setSwapInProgress(true);
      setSelectedBackground(backgroundImageUrl);

      const headers = {};
      if (currentUser) {
        headers['Authorization'] = `Bearer ${await currentUser.getIdToken()}`;
        headers['X-User-Id'] = currentUser.uid;
      }

      if (backgroundImageUrl === "transparent") {
        setDisplayImage(removedBgImage);
        setSwapInProgress(false);
        return;
      }

      const backgroundResponse = await fetch(backgroundImageUrl);
      const backgroundBlob = await backgroundResponse.blob();
      const backgroundBase64 = await convertBlobToBase64(backgroundBlob);

      const foregroundBase64 = removedBgImage.split(",")[1];
      const backgroundBase64Data = backgroundBase64.split(",")[1];

      const response = await postBackgroundSwap({
        foreground_image: foregroundBase64,
        background_image: backgroundBase64Data,
        headers,
      });

      if (response.data.image) {
        setDisplayImage(`data:image/png;base64,${response.data.image}`);
      }
    } catch (error) {
      console.error("Error swapping background:", error);
      alert("Error swapping background. Please try again.");
    } finally {
      setSwapInProgress(false);
    }
  };

  const handleCustomBackgroundUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomBackground(reader.result);
        handleBackgroundSelect(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // processNewImage function to handle new image uploads

  const processNewImage = useCallback(async (imageData) => {
    try {
      const base64String = imageData.split(",")[1];
      setLoading(true);
      setImageLoaded(false);

      const headers = {};
      if (currentUser) {
        headers['Authorization'] = `Bearer ${await currentUser.getIdToken()}`;
        headers['X-User-Id'] = currentUser.uid;
      }

      const response = await postBackgroundRemove({
        image: base64String,
        headers,
      });

      if (response.data.image) {
        const newProcessedImage = `data:image/png;base64,${response.data.image}`;
        setRemovedBgImage(newProcessedImage);
        setDisplayImage(newProcessedImage);
        setSelectedBackground(null);

        const img = new Image();
      img.src = newProcessedImage;
      img.onload = () => setImageLoaded(true);
      }
    } catch (error) {
      console.error("Error processing image:", error);
      alert("Error processing image. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const handleNewImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const imageUrl = reader.result;
        setImage(imageUrl);
        processNewImage(imageUrl);
      };
    }
  };

  const setImage = (imageUrl) => {
    // Update original image reference
    if (onNewUpload && typeof onNewUpload === 'function') {
      // If the parent component provides a way to update the original image
      onNewUpload(imageUrl);
    }
  };

  


  // const handlePromptSubmit = async (e) => {
  //   e.preventDefault();
  //   if (!backgroundPrompt.trim()) return;

  //   try {
  //     setSwapInProgress(true);
  //     const foregroundBase64 = removedBgImage.split(",")[1];

  //     const response = await axios.post("https://api.gomens.ai/api/v1/background/swap", {
  //       foreground_image: foregroundBase64,
  //       background_prompt: backgroundPrompt,
  //     });

  //     if (response.data.image) {
  //       setDisplayImage(`data:image/png;base64,${response.data.image}`);
  //       setSelectedBackground(null);
  //     }
  //   } catch (error) {
  //     console.error("Error generating background from prompt:", error);
  //     alert("Error generating background. Please try again with a different prompt.");
  //   } finally {
  //     setSwapInProgress(false);
  //   }
  // };

  const convertBlobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = displayImage;
    link.download = "background-modified.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSliderChange = (value) => {
    setSliderPosition(value);
  };

  return (
    <div className="flex-1 h-full overflow-auto relative z-10 ">
      {/* Header */}
      <header className="mx-auto py-3 px-4 sm:px-6 bg-white shadow-md border-b border-blue-100 sticky top-0 z-20">
        <div className="flex items-center sm:flex-row sm:items-center gap-3 sm:gap-5">
          <button
            onClick={onBackClick}
            className="p-2 rounded-full hover:bg-blue-50 transition-colors text-blue-600 w-fit"
            aria-label="Go back"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold bg-gradient-to-r from-violet-600 to-blue-500 text-transparent bg-clip-text">
            Background Remover 
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row w-full p-2 sm:p-4 lg:p-6 gap-4 lg:gap-8">
        {/* Image Comparison Section */}
        <div className="flex-1 flex flex-col items-center">
          <div className="bg-white rounded-xl shadow-lg p-3 sm:p-5 w-full mb-2 border border-blue-100">
            <h2 className="text-base sm:text-lg font-medium mb-2">
              {loading || swapInProgress ? "Processing..." : "Compare Results"}
            </h2>
            <div className="w-full h-48 sm:h-64 md:h-[50vh] lg:h-[60vh]  bg-cover bg-center rounded border border-gray-200 flex items-center justify-center overflow-hidden relative">
              {loading || swapInProgress ? (
                <div className="flex flex-col items-center justify-center h-full w-full">
                  <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                  <p className="text-gray-500 text-sm sm:text-base">
                    {loading ? "Removing background..." : "Swapping background..."}
                  </p>
                </div>
              ) : displayImage ? (
                <div className="relative w-full h-full">
                  <img
                    src={displayImage}
                    alt="Processed Image"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  <div
                    className="absolute inset-0 overflow-hidden"
                    style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                  >
                    <img
                      src={originalImage}
                      alt="Original"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="absolute top-2 left-2 bg-black bg-opacity-60 text-white rounded px-2 py-1 text-xs sm:text-sm">
                    Original
                  </div>
                  <div className="absolute top-2 right-2 bg-blue-600 bg-opacity-60 text-white rounded px-2 py-1 text-xs sm:text-sm">
                    Processed
                  </div>
                  <div
                    className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize flex items-center justify-center"
                    style={{ left: `${sliderPosition}%`, transform: "translateX(-50%)" }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const startX = e.clientX;
                      const startPosition = sliderPosition;
                      const handleMouseMove = (e) => {
                        const deltaX = e.clientX - startX;
                        const newPosition = Math.min(100, Math.max(0, startPosition + (deltaX / 2)));
                        handleSliderChange(newPosition);
                      };
                      const handleMouseUp = () => {
                        window.removeEventListener("mousemove", handleMouseMove);
                        window.removeEventListener("mouseup", handleMouseUp);
                      };
                      window.addEventListener("mousemove", handleMouseMove);
                      window.addEventListener("mouseup", handleMouseUp);
                    }}
                  >
                    <div className="bg-white text-blue-600 p-1 sm:p-2 rounded-full shadow-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-6 sm:w-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                        <path d="M15.59 16.59L10.42 12 15.59 7.41 14.17 6l-6 6 6 6 1.42-1.41z" transform="scale(-1, 1) translate(-24, 0)" />
                      </svg>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full w-full">
                  <p className="text-gray-400 text-sm sm:text-base">Processing image...</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="flex-1 lg:max-w-[400px] space-y-4 sm:space-y-6">
          {/* Background Selection */}
          <div className="bg-white rounded-xl shadow-lg p-3 sm:p-5 w-full mb-2 border border-blue-100">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-700 mb-3 sm:mb-4">Choose a Background</h1>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div
                className={`relative cursor-pointer overflow-hidden rounded-lg border-2 hover:border-blue-500 transition-all ${
                  selectedBackground === "transparent" ? "border-blue-500 ring-2 ring-blue-300" : "border-gray-200"
                }`}
                onClick={() => !swapInProgress && handleBackgroundSelect("transparent")}
              >
                <div className="w-full h-20 sm:h-24 bg-gray-100 flex flex-col items-center justify-center">
                  <span className="text-xs sm:text-sm text-gray-600">Transparent</span>
                </div>
              </div>
              {backgroundImages.map((bgImage, index) => (
                <div
                  key={index}
                  className={`relative cursor-pointer overflow-hidden rounded-lg border-2 hover:border-blue-500 transition-all ${
                    selectedBackground === bgImage ? "border-blue-500 ring-2 ring-blue-300" : "border-gray-200"
                  }`}
                  onClick={() => !swapInProgress && handleBackgroundSelect(bgImage)}
                >
                  <img
                    src={bgImage}
                    alt={`Background option ${index + 1}`}
                    className="w-full h-20 sm:h-24 object-cover"
                  />
                </div>
              ))}
              <div
                className="relative cursor-pointer overflow-hidden rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-all"
                onClick={() => fileInputRef.current.click()}
              >
                <div className="w-full h-20 sm:h-24 bg-gray-100 flex flex-col items-center justify-center">
                  <Upload size={20} className="text-gray-400 mb-1 sm:mb-2" />
                  <span className="text-xs sm:text-sm text-gray-600">Custom</span>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleCustomBackgroundUpload}
                />
              </div>
            </div>
          </div>

          {/* Background Generator */}
          {/* <div className="bg-blue-50 rounded-lg p-3 sm:p-4 shadow-sm">
            <h2 className="text-lg sm:text-xl font-medium mb-2 sm:mb-3">Generate Custom Background</h2>
            <form onSubmit={handlePromptSubmit}>
              <textarea
                className="w-full p-3 sm:p-4 bg-blue-100 shadow-2xl rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm sm:text-base"
                placeholder="Example: Blue sky with fluffy clouds, mountain landscape..."
                rows="3"
                value={backgroundPrompt}
                onChange={(e) => setBackgroundPrompt(e.target.value)}
                disabled={swapInProgress}
              />
              <button
                type="submit"
                className="mt-2 sm:mt-3 w-full py-2 sm:py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors text-sm sm:text-base"
                disabled={swapInProgress || !backgroundPrompt.trim()}
              >
                {swapInProgress ? "Generating..." : "Generate Background"}
              </button>
            </form>
          </div> */}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 p-4 sm:p-6">
        <button
          onClick={handleDownload}
          disabled={!displayImage || loading || swapInProgress}
          className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors w-full sm:w-auto justify-center text-sm sm:text-base"
        >
          <Download size={18} />
          Download Image
        </button>

        <label
          htmlFor="new-image-upload"
          className={`flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 ${
            loading || swapInProgress 
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300 cursor-pointer"
          } rounded-lg transition-colors w-full sm:w-auto justify-center text-sm sm:text-base`}
        >
          <Upload size={18} />
          Upload New Image
          <input
            id="new-image-upload"
            type="file"
            ref={newImageInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleNewImageUpload}
            disabled={loading || swapInProgress}
          />
        </label>
      </div>
    </div>
  );
};

export default BackgroundRemoverResult;